/**
 * LocationSharingDisclosureSheet — prominent in-app disclosure shown BEFORE
 * requesting foreground / background location permissions.
 *
 * Required by the Google Play User Data policy:
 *   https://support.google.com/googleplay/android-developer/answer/10144311#prominent-disclosure
 *
 * The disclosure must appear in the normal flow of the app (not only in the
 * privacy policy / settings description) and must clearly state what data is
 * collected, how it is used, and that collection may occur "even when the app
 * is closed or not in use" for background location.
 *
 * Modes:
 *   - 'consent' (default) — single "Continue" button that hands off to the OS
 *     permission prompt via onEnable. Per Apple guideline 5.1.1(iv), once this
 *     disclosure is shown the user must always proceed to the system prompt —
 *     no opt-out button, no backdrop-tap-to-dismiss, no onRequestClose. The
 *     yes/no decision is made by the user in the native permission dialog.
 *   - 'info'             — single dismiss button ("Got it"), used when the
 *     user taps the "Read disclosure" entry point in Settings to re-read the
 *     disclosure after having already opted in.
 */

import React, { useMemo } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../hooks/useTheme';
import { useSheetLayout } from '../utilities/sheetLayout';
import Icon from './Icon';
import type { AppTheme } from '../theme';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';

interface Props {
  visible: boolean;
  mode?: 'consent' | 'info';
  onClose: () => void;
  onEnable?: () => void;
}

interface FeatureRow {
  icon: IconProp;
  color: string;
  title: string;
  body: string;
}

export default function LocationSharingDisclosureSheet({
  visible,
  mode = 'consent',
  onClose,
  onEnable,
}: Props) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const sheet = useSheetLayout({ desktopMaxWidth: 480 });
  const styles = useMemo(() => makeStyles(theme, insets), [theme, insets]);

  const FEATURES = useMemo<FeatureRow[]>(() => [
    {
      icon: ['fad', 'location-dot'],
      color: '#0EA5E9',
      title: 'What we collect',
      body: 'Your device\u2019s precise location. On Android, location may be collected in the background \u2014 that is, even when the app is closed or not in use \u2014 so other members of your projects can see your last-known location.',
    },
    {
      icon: ['fad', 'shield-heart'],
      color: '#EF4444',
      title: 'Why we collect it',
      body: 'So caregivers and family members you\u2019ve invited to your projects can see that you\u2019re safe, find your last known location in an emergency, and show you on the People map.',
    },
    {
      icon: ['fad', 'users-gear'],
      color: '#8B5CF6',
      title: 'Who can see it',
      body: 'Only members of projects you belong to. Your location is never sold and is never shared with advertisers or third-party services.',
    },
    {
      icon: ['fad', 'sliders'],
      color: '#10B981',
      title: 'You\u2019re in control',
      body: 'Turn location sharing off any time with Location Sharing in Account settings. You can also fully revoke the permission in your device settings.',
    },
  ], []);

  const showConsentButtons = mode === 'consent';
  const handleModalRequestClose = showConsentButtons ? () => {} : onClose;

  return (
    <Modal
      visible={visible}
      animationType={sheet.animationType}
      transparent
      onRequestClose={handleModalRequestClose}
    >
      <View style={[styles.overlay, sheet.overlayStyle]}>
        <View style={[styles.sheet, sheet.sheetStyle]}>
          {sheet.showHandle && <View style={styles.handle} />}
          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={[styles.content, sheet.desktopContentStyle]}
          >
            <View style={styles.hero}>
              <View style={styles.heroIconWrap}>
                <Icon icon={['fad', 'location-dot']} size={32} color="#0EA5E9" />
              </View>
              <Text style={styles.heroTitle}>Location sharing</Text>
              <Text style={styles.heroSubtitle}>
                {'Before you turn on location sharing, here\u2019s exactly what Eleplan collects, how it\u2019s used, and who it\u2019s shared with.'}
              </Text>
            </View>

            <Text style={styles.sectionLabel}>The details</Text>
            {FEATURES.map((f) => (
              <View key={f.title} style={styles.featureRow}>
                <View style={[styles.featureIcon, { backgroundColor: `${f.color}18` }]}>
                  <Icon icon={f.icon} size={17} color={f.color} />
                </View>
                <View style={styles.featureText}>
                  <Text style={styles.featureTitle}>{f.title}</Text>
                  <Text style={styles.featureBody}>{f.body}</Text>
                </View>
              </View>
            ))}

            <Text style={styles.legalNote}>
              {'After tapping Continue, your device will ask whether to allow Eleplan to access your location. You can approve or decline there, and change it any time in device settings. See the Privacy Policy for full details.'}
            </Text>
          </ScrollView>

          <View style={styles.ctaWrap}>
            {showConsentButtons ? (
              <TouchableOpacity
                style={styles.ctaBtn}
                onPress={() => { onEnable?.(); }}
                activeOpacity={0.85}
              >
                <Text style={styles.ctaBtnText}>Continue</Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity style={styles.ctaBtn} onPress={onClose} activeOpacity={0.85}>
                <Text style={styles.ctaBtnText}>Got it</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
}

const makeStyles = (theme: AppTheme, insets: { bottom: number }) =>
  StyleSheet.create({
    overlay: {
      flex: 1,
      justifyContent: 'flex-end',
      backgroundColor: 'rgba(0,0,0,0.45)',
    },
    sheet: {
      backgroundColor: theme.colors.card,
      borderTopLeftRadius: 22,
      borderTopRightRadius: 22,
      maxHeight: '90%',
    },
    handle: {
      width: 36, height: 4, borderRadius: 2,
      backgroundColor: theme.colors.border,
      alignSelf: 'center',
      marginTop: 10, marginBottom: 4,
    },
    content: {
      paddingHorizontal: theme.spacing.md,
      paddingTop: theme.spacing.sm,
      paddingBottom: theme.spacing.lg,
    },
    hero: {
      alignItems: 'center',
      paddingVertical: theme.spacing.md,
      marginBottom: theme.spacing.md,
    },
    heroIconWrap: {
      width: 72, height: 72, borderRadius: 36,
      backgroundColor: 'rgba(14,165,233,0.12)',
      justifyContent: 'center', alignItems: 'center',
      marginBottom: theme.spacing.md,
    },
    heroTitle: {
      fontSize: theme.font.size.xl,
      fontWeight: theme.font.weight.bold,
      color: theme.colors.text,
      marginBottom: 6,
      textAlign: 'center',
    },
    heroSubtitle: {
      fontSize: theme.font.size.sm,
      color: theme.colors.muted,
      textAlign: 'center',
      lineHeight: 20,
      paddingHorizontal: theme.spacing.sm,
    },
    sectionLabel: {
      fontSize: theme.font.size.xs,
      fontWeight: theme.font.weight.semibold,
      color: theme.colors.muted,
      textTransform: 'uppercase',
      letterSpacing: 0.6,
      marginBottom: theme.spacing.sm,
      marginTop: theme.spacing.xs,
    },
    featureRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: theme.spacing.md,
      gap: 14,
    },
    featureIcon: {
      width: 40, height: 40, borderRadius: 20,
      justifyContent: 'center', alignItems: 'center',
      flexShrink: 0,
    },
    featureText: { flex: 1 },
    featureTitle: {
      fontSize: theme.font.size.md,
      fontWeight: theme.font.weight.semibold,
      color: theme.colors.text,
      marginBottom: 2,
    },
    featureBody: {
      fontSize: theme.font.size.sm,
      color: theme.colors.muted,
      lineHeight: 19,
    },
    legalNote: {
      fontSize: theme.font.size.xs,
      color: theme.colors.muted,
      textAlign: 'center',
      lineHeight: 17,
      marginTop: theme.spacing.md,
      paddingHorizontal: theme.spacing.md,
    },
    ctaWrap: {
      paddingHorizontal: theme.spacing.md,
      paddingTop: theme.spacing.sm,
      paddingBottom: insets.bottom + 12,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: theme.colors.border,
      backgroundColor: theme.colors.card,
    },
    ctaBtn: {
      backgroundColor: theme.colors.primary,
      borderRadius: theme.radius.md,
      paddingVertical: 13,
      alignItems: 'center',
    },
    ctaBtnText: {
      fontSize: theme.font.size.md,
      fontWeight: theme.font.weight.semibold,
      color: theme.colors.primaryText,
    },
    secondaryBtn: {
      paddingVertical: 12,
      alignItems: 'center',
      marginTop: 6,
    },
    secondaryBtnText: {
      fontSize: theme.font.size.md,
      fontWeight: theme.font.weight.semibold,
      color: theme.colors.muted,
    },
  });
